/*
 * The React binding of the editor (F-2 §6.3): one `EditorView` per mounted component, created into
 * a host element React never renders into. The rules that make it a composer and not a demo:
 *
 * - **The draft is the authority.** The view serializes its document into the draft on every
 *   change, and when the draft changes to something the view did not write — the disk answering
 *   after a reload, an edit's seed, a second tab, a message taken back out of the outbox, a
 *   surface's `setValue` — the view replaces its document with the draft's markdown and puts the
 *   caret at the end. One rule for every way text can arrive from outside.
 * - **Nothing happens mid-composition.** Serialization and replacement are deferred to
 *   `compositionend`, so an IME's candidate is never torn out from under the person typing it.
 * - **A sent message is gone.** When the draft is cleared, or the target changes, the state is
 *   created afresh with an empty history, so Ctrl+Z cannot resurrect what has left.
 * - `data-markdown` on the host mirrors the last serialized draft, for tests and debugging.
 */

import { EditorState, Selection, type Plugin } from "prosemirror-state"
import { EditorView, type EditorProps } from "prosemirror-view"
import { useCallback, useEffect, useLayoutEffect, useRef, useSyncExternalStore } from "react"
import { parseMarkdown, schema, serializeMarkdown } from "./message.ts"
import { selectionSyncEvents } from "./selectionSync.ts"

export interface EditorBindingOptions {
	/** The draft's text — the authority the document follows. */
	value: string
	/** False until the disk has answered for this draft; an empty value before that means nothing. */
	ready: boolean
	/** A change of target: the state is recreated, history and all. */
	resetKey: string
	editable: boolean
	/** The serialized document after every change made in the view. */
	onChange: (markdown: string) => void
	/** Built once per view; the bindings inside read whatever they need through refs. */
	plugins: () => Plugin[]
	/** The handlers, read per event so they may close over the latest render. */
	handlers: () => Pick<
		EditorProps,
		"handleKeyDown" | "handlePaste" | "handleDOMEvents" | "clipboardTextSerializer"
	>
}

export interface EditorBinding {
	view: () => EditorView | null
	/** The state as of the last transaction; null before the view exists. */
	state: EditorState | null
	/** The markdown the view last wrote or adopted. */
	markdown: () => string
	focus: () => void
	/** The host's attributes — the accessible name and the combobox state while a list is open. */
	setAttributes: (attrs: Record<string, string>) => void
}

/** @param host the element the view is created into; the component renders it and nothing else. */
export function useEditorView(
	host: React.RefObject<HTMLDivElement | null>,
	options: EditorBindingOptions,
): EditorBinding {
	const viewRef = useRef<EditorView | null>(null)
	const latest = useRef(options)
	const lastMarkdown = useRef(options.value)
	// A change that arrived while an IME was composing, adopted on `compositionend`.
	const deferred = useRef<{ external: string | null; serialize: boolean }>({
		external: null,
		serialize: false,
	})
	const listeners = useRef(new Set<() => void>())

	useLayoutEffect(() => {
		latest.current = options
	})

	const notify = useCallback(() => {
		for (const listener of listeners.current) listener()
	}, [])

	const mirror = useCallback((markdown: string) => {
		lastMarkdown.current = markdown
		viewRef.current?.dom.setAttribute("data-markdown", markdown)
	}, [])

	/** Writes the document into the draft — unless the draft already says exactly this. */
	const serialize = useCallback(
		(state: EditorState) => {
			const markdown = serializeMarkdown(state.doc)
			mirror(markdown)
			if (markdown !== latest.current.value) latest.current.onChange(markdown)
		},
		[mirror],
	)

	const freshState = useCallback(
		(markdown: string, plugins: readonly Plugin[]) =>
			EditorState.create({ schema, doc: parseMarkdown(markdown), plugins }),
		[],
	)

	/** The draft changed under the view: the document follows it, the caret goes to the end. */
	const adopt = useCallback(
		(markdown: string) => {
			const view = viewRef.current
			if (view === null) return
			mirror(markdown)
			if (markdown === "") {
				// Cleared from outside — after a send, above all: a fresh history, so undo cannot
				// bring the message back.
				view.updateState(freshState("", view.state.plugins))
				notify()
				return
			}
			const doc = parseMarkdown(markdown)
			const tr = view.state.tr.replaceWith(0, view.state.doc.content.size, doc.content)
			tr.setSelection(Selection.atEnd(tr.doc)).setMeta("addToHistory", false)
			view.dispatch(tr)
		},
		[freshState, mirror, notify],
	)

	// One view for the life of the component.
	useLayoutEffect(() => {
		const element = host.current
		if (element === null) return
		const opts = latest.current
		lastMarkdown.current = opts.value
		const view = new EditorView(element, {
			state: freshState(opts.value, opts.plugins()),
			editable: () => latest.current.editable,
			dispatchTransaction(tr) {
				const next = view.state.apply(tr)
				view.updateState(next)
				if (tr.docChanged) {
					if (view.composing) deferred.current.serialize = true
					else serialize(next)
				}
				notify()
			},
			handleKeyDown: (v, event) => latest.current.handlers().handleKeyDown?.(v, event) ?? false,
			handlePaste: (v, event, slice) =>
				latest.current.handlers().handlePaste?.(v, event, slice) ?? false,
			clipboardTextSerializer: (slice, v) =>
				latest.current.handlers().clipboardTextSerializer?.(slice, v) ??
				slice.content.textBetween(0, slice.content.size, "\n\n"),
			handleDOMEvents: {
				compositionend: (v) => {
					// The view clears `composing` after this event; what was deferred runs then.
					queueMicrotask(() => {
						if (v.composing || viewRef.current !== v) return
						const pending = deferred.current
						deferred.current = { external: null, serialize: false }
						if (pending.serialize) serialize(v.state)
						if (pending.external !== null) adopt(pending.external)
					})
					return false
				},
				// The surface answers Escape here and not in the keymap: prosemirror-view claims the
				// key whether or not a command took it (`captureKeyDown`), and a handler that returns
				// true from a DOM event is the one place the view's own keydown handling — and its
				// preventDefault with it — is skipped altogether.
				keydown: (v, event) =>
					latest.current.handlers().handleDOMEvents?.keydown?.(v, event) ?? false,
				focus: (v, event) => latest.current.handlers().handleDOMEvents?.focus?.(v, event) ?? false,
				blur: (v, event) => latest.current.handlers().handleDOMEvents?.blur?.(v, event) ?? false,
				// The repair that keeps the view's idea of the caret honest where the browser fires no
				// `selectionchange` of its own (`selectionSync.ts`).
				...selectionSyncEvents(),
			},
		})
		// The mirror sits on the view's own element — the one the composer's class names and the
		// tests address — not on the wrapper the view was created into.
		view.dom.setAttribute("data-markdown", opts.value)
		viewRef.current = view
		notify()
		return () => {
			viewRef.current = null
			view.destroy()
		}
	}, [adopt, freshState, host, notify, serialize])

	// The authority rule: a draft the view did not write replaces the document.
	const { value, ready, resetKey } = options
	useEffect(() => {
		const view = viewRef.current
		if (view === null || !ready || value === lastMarkdown.current) return
		if (view.composing) deferred.current.external = value
		else adopt(value)
	}, [adopt, ready, value])

	// A new target is a new document with nothing to undo into.
	const firstKey = useRef(resetKey)
	useEffect(() => {
		const view = viewRef.current
		if (view === null || firstKey.current === resetKey) return
		firstKey.current = resetKey
		mirror(latest.current.value)
		view.updateState(freshState(latest.current.value, view.state.plugins))
		notify()
	}, [freshState, mirror, notify, resetKey])

	const subscribe = useCallback((listener: () => void) => {
		listeners.current.add(listener)
		return () => listeners.current.delete(listener)
	}, [])
	// The snapshot is the state itself: a new object per transaction, the same one until the next.
	const state = useSyncExternalStore(subscribe, () => viewRef.current?.state ?? null)

	return {
		view: useCallback(() => viewRef.current, []),
		state,
		markdown: useCallback(() => lastMarkdown.current, []),
		focus: useCallback(() => viewRef.current?.focus(), []),
		setAttributes: useCallback((attributes: Record<string, string>) => {
			viewRef.current?.setProps({ attributes })
		}, []),
	}
}
